/**
 * Command Center constants — maps to backend EnumCommandType, EnumCommandCategory, etc.
 */

// Device classes selectable in the Command Center. Each command category and
// command below belongs to exactly one class; the asset list is fetched with
// the class's assetType. Trackers stay ONE asset type (GT06_TRACKER) — the
// per-asset `protocol` field ("FT920" vs null/"GT06") decides remote-command
// eligibility, not a separate device type.
export const DEVICE_CLASSES = [
  { key: "LOCK", label: "GPS Locks", assetType: "GPS_LOCK" },
  { key: "TRACKER", label: "GPS Trackers", assetType: "GT06_TRACKER" },
];

export const COMMAND_CATEGORIES = [
  { key: "QUERY", label: "Device Query", icon: "faSearch", deviceClass: "LOCK" },
  { key: "LOCK_CONTROL", label: "Lock Control", icon: "faLock", deviceClass: "LOCK" },
  { key: "CONFIG", label: "Reporting Config", icon: "faGear", deviceClass: "LOCK" },
  { key: "RFID", label: "RFID Management", icon: "faIdCard", deviceClass: "LOCK" },
  { key: "ALERTS", label: "Alert Config", icon: "faBell", deviceClass: "LOCK" },
  { key: "GEOFENCE", label: "Geofence", icon: "faMapPin", deviceClass: "LOCK" },
  { key: "DEVICE_CONTROL", label: "Device Control", icon: "faServer", deviceClass: "LOCK" },
  // FT920 (JT808) tracker categories — mirror backend EnumCommandCategory.
  { key: "TRACKER_QUERY", label: "Device Query", icon: "faSearch", deviceClass: "TRACKER" },
  { key: "TRACKER_CONFIG", label: "Tracker Config", icon: "faGear", deviceClass: "TRACKER" },
  { key: "TRACKER_CONTROL", label: "Vehicle Control", icon: "faServer", deviceClass: "TRACKER" },
];

export const SAFETY_LEVELS = {
  SAFE: { color: "#10b981", label: "Safe" },
  CAUTION: { color: "#f59e0b", label: "Caution" },
  WARNING: { color: "#ff683a", label: "Warning" },
  DANGEROUS: { color: "#ef4444", label: "Dangerous" },
};

export const DELIVERY_MODES = {
  IMMEDIATE_ONLY: "Immediate Only",
  QUEUE_WHEN_OFFLINE: "Queue When Offline",
};

// Role access levels
export const ROLE_ACCESS = {
  CLIENT_EMPLOYEE: ["QUERY", "TRACKER_QUERY"],
  CLIENT_MANAGER: [
    "QUERY", "LOCK_CONTROL", "CONFIG", "RFID", "ALERTS", "GEOFENCE",
    "TRACKER_QUERY", "TRACKER_CONFIG",
  ],
  CLIENT_ADMIN: [
    "QUERY", "LOCK_CONTROL", "CONFIG", "RFID", "ALERTS", "GEOFENCE", "DEVICE_CONTROL",
    "TRACKER_QUERY", "TRACKER_CONFIG", "TRACKER_CONTROL",
  ],
  SUPER_ADMIN: [
    "QUERY", "LOCK_CONTROL", "CONFIG", "RFID", "ALERTS", "GEOFENCE", "DEVICE_CONTROL",
    "TRACKER_QUERY", "TRACKER_CONFIG", "TRACKER_CONTROL",
  ],
};

// Command definitions — each entry maps to backend EnumCommandType
export const COMMANDS = {
  // Device Query
  QUERY_FIRMWARE: {
    label: "Query Firmware",
    category: "QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  QUERY_POSITION: {
    label: "Query Position",
    category: "QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  QUERY_IMEI: {
    label: "Query IMEI",
    category: "QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  QUERY_STATUS: {
    label: "Query Real-time Status",
    category: "QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },

  // Lock Control
  UNLOCK: {
    label: "Unlock Device",
    category: "LOCK_CONTROL",
    safety: "CAUTION",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
    warning: "This will physically unlock the device. Ensure authorized personnel are on-site.",
  },
  CHANGE_PASSWORD: {
    label: "Change Unlock Password",
    category: "LOCK_CONTROL",
    safety: "CAUTION",
    delivery: "IMMEDIATE_ONLY",
    fields: [
      { key: "newPassword", label: "New Password (6 chars)", type: "text", maxLength: 6, required: true },
    ],
    warning: "Changing the password will immediately affect all future unlock attempts.",
  },

  // Reporting Config
  QUERY_WORKING_MODE: {
    label: "Query Working Mode",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_WORKING_MODE: {
    label: "Set Working Mode",
    category: "CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      {
        key: "mode",
        label: "Working Mode",
        type: "select",
        options: [
          { value: 0, label: "SMS Mode" },
          { value: 1, label: "Power Saving" },
          { value: 2, label: "Real-time Tracking" },
          { value: 8, label: "Lock/Unlock Mode" },
        ],
        required: true,
      },
    ],
    warning: "Real-time mode significantly increases battery consumption.",
  },
  QUERY_REPORT_INTERVAL: {
    label: "Query Report Interval",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_REPORT_INTERVAL: {
    label: "Set Report Interval",
    category: "CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "reportInterval", label: "Report Interval (5-600 sec)", type: "number", min: 5, max: 600, required: true },
      { key: "wakeUpInterval", label: "Wake-up Interval (30-1440 min)", type: "number", min: 30, max: 1440, required: true },
    ],
  },
  QUERY_WAKEUP_DURATION: {
    label: "Query Wake-up Duration",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_WAKEUP_DURATION: {
    label: "Set Wake-up Duration",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "duration", label: "Duration (3-10 min)", type: "number", min: 3, max: 10, required: true },
    ],
  },
  QUERY_LOCK_UNLOCK_INTERVAL: {
    label: "Query Lock/Unlock Report Interval",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_LOCK_UNLOCK_INTERVAL: {
    label: "Set Lock/Unlock Report Interval",
    category: "CONFIG",
    safety: "SAFE",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "lockInterval", label: "Lock Mode Interval (sec)", type: "number", min: 5, max: 600, required: true },
      { key: "unlockInterval", label: "Unlock Mode Interval (sec)", type: "number", min: 5, max: 600, required: true },
    ],
  },

  // RFID Management
  QUERY_RFID_KEYS: {
    label: "List RFID Keys",
    category: "RFID",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [
      {
        key: "groupIndex",
        label: "Group",
        type: "select",
        options: [
          { value: 1, label: "Group 1" },
          { value: 2, label: "Group 2" },
          { value: 3, label: "Group 3" },
        ],
        required: true,
      },
    ],
  },
  ADD_RFID_KEY: {
    label: "Register RFID Key",
    category: "RFID",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "rfidKeys", label: "RFID Key Numbers (comma-separated)", type: "textarea", required: true },
    ],
    warning: "Adding RFID keys allows those cards to unlock the device.",
  },
  DELETE_RFID_KEY: {
    label: "Remove RFID Key",
    category: "RFID",
    safety: "WARNING",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "rfidKeys", label: "RFID Key Numbers to delete (comma-separated)", type: "textarea", required: true },
    ],
    warning: "Removing RFID keys will immediately prevent those cards from unlocking.",
  },
  DELETE_ALL_RFID_KEYS: {
    label: "Clear All RFID Keys",
    category: "RFID",
    safety: "DANGEROUS",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [],
    warning: "This will remove ALL authorized RFID keys. No one will be able to unlock via RFID until new keys are registered.",
    requireConfirmText: true,
  },

  // Alert Config
  QUERY_ALERT_SWITCHES: {
    label: "Query Alert Switches",
    category: "ALERTS",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_ALERT_SWITCHES: {
    label: "Set Alert Switches",
    category: "ALERTS",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "ropeTamper", label: "Lock Rope Tamper", type: "alertSwitch" },
      { key: "unauthRfid", label: "Unauthorized RFID", type: "alertSwitch" },
      { key: "unlock", label: "Unlocking Alert", type: "alertSwitch" },
      { key: "wrongPwd", label: "Wrong Password", type: "alertSwitch" },
      { key: "vibration", label: "Vibration", type: "alertSwitch" },
      { key: "enterGeo", label: "Enter Geo-fence", type: "alertSwitch" },
      { key: "exitGeo", label: "Exit Geo-fence", type: "alertSwitch" },
      { key: "lowBattery", label: "Low Battery", type: "alertSwitch" },
      { key: "backCover", label: "Back Cover Opened", type: "alertSwitch" },
      { key: "motorFault", label: "Motor Fault", type: "alertSwitch" },
      { key: "jamming", label: "Jamming", type: "alertSwitch" },
    ],
    warning: "Disabling alerts will suppress security notifications.",
  },
  QUERY_VIBRATION_SENSITIVITY: {
    label: "Query Vibration Sensitivity",
    category: "ALERTS",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_VIBRATION_SENSITIVITY: {
    label: "Set Vibration Sensitivity",
    category: "ALERTS",
    safety: "SAFE",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "threshold", label: "Threshold (500-900 mg)", type: "number", min: 500, max: 900, required: true },
    ],
  },
  QUERY_UNLOCK_ALERT_INTERVAL: {
    label: "Query Unlock Alert Interval",
    category: "ALERTS",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_UNLOCK_ALERT_INTERVAL: {
    label: "Set Unlock Alert Interval",
    category: "ALERTS",
    safety: "SAFE",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "interval", label: "Interval (minutes)", type: "number", min: 1, max: 1440, required: true },
    ],
  },

  // Geofence
  QUERY_RFID_GEOFENCE_LINK: {
    label: "Query RFID-Geofence Link",
    category: "GEOFENCE",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_RFID_GEOFENCE_LINK: {
    label: "Set RFID-Geofence Link",
    category: "GEOFENCE",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      {
        key: "enable",
        label: "RFID Unlock Restricted to Geo-fence",
        type: "select",
        options: [
          { value: 1, label: "Yes - Only inside geo-fence" },
          { value: 0, label: "No - Anywhere" },
        ],
        required: true,
      },
    ],
    warning: "Disabling restriction allows RFID unlock from any location.",
  },

  // Device Control
  REBOOT: {
    label: "Reboot Device",
    category: "DEVICE_CONTROL",
    safety: "DANGEROUS",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
    warning: "Device will reboot in 30 seconds and be unreachable during restart.",
    requireConfirmText: true,
  },
  FORCE_SLEEP: {
    label: "Force Sleep",
    category: "DEVICE_CONTROL",
    safety: "DANGEROUS",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
    warning: "Device will enter sleep mode immediately. Unreachable until next wake-up cycle.",
    requireConfirmText: true,
  },
  QUERY_POWER_SWITCH: {
    label: "Query Power Switch",
    category: "DEVICE_CONTROL",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_POWER_SWITCH: {
    label: "Set Power Switch",
    category: "DEVICE_CONTROL",
    safety: "DANGEROUS",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      {
        key: "enable",
        label: "Power Switch",
        type: "select",
        options: [
          { value: 1, label: "Enabled (can be turned off manually)" },
          { value: 0, label: "Disabled (cannot be turned off manually)" },
        ],
        required: true,
      },
    ],
    warning: "Disabling the power switch means the device cannot be manually turned off.",
    requireConfirmText: true,
  },
  QUERY_ROPE_CUT_REMINDER: {
    label: "Query Rope Cut Reminder",
    category: "DEVICE_CONTROL",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  SET_ROPE_CUT_REMINDER: {
    label: "Set Rope Cut Reminder",
    category: "DEVICE_CONTROL",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      {
        key: "enable",
        label: "Rope Cut Reminder (Blink & Beep)",
        type: "select",
        options: [
          { value: 1, label: "Enabled" },
          { value: 0, label: "Disabled" },
        ],
        required: true,
      },
    ],
  },
};

// ── FT920 (JT808) tracker commands ─────────────────────────────────────────
// Field ranges come straight from the FT920 protocol document (Table 11);
// out-of-range values are rejected by both the backend and the device.
Object.assign(COMMANDS, {
  TRACKER_QUERY_PARAMS: {
    label: "Read Device Parameters",
    category: "TRACKER_QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  TRACKER_QUERY_LOCATION: {
    label: "Poll Current Position",
    category: "TRACKER_QUERY",
    safety: "SAFE",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
  },
  TRACKER_SET_HEARTBEAT: {
    label: "Set Heartbeat Interval",
    category: "TRACKER_CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "seconds", label: "Heartbeat interval (60-1800 sec)", type: "number", min: 60, max: 1800, required: true },
    ],
  },
  TRACKER_SET_REPORT_INTERVAL: {
    label: "Set Location Report Interval",
    category: "TRACKER_CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "seconds", label: "Report interval (3-10800 sec)", type: "number", min: 3, max: 10800, required: true },
    ],
    warning: "Very short intervals increase data usage and battery drain.",
  },
  TRACKER_SET_SLEEP_INTERVAL: {
    label: "Set Sleep Report Interval",
    category: "TRACKER_CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "seconds", label: "Sleep report interval (3-10800 sec)", type: "number", min: 3, max: 10800, required: true },
    ],
  },
  TRACKER_SET_OVERSPEED: {
    label: "Set Device Overspeed Limit",
    category: "TRACKER_CONFIG",
    safety: "CAUTION",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [
      { key: "maxSpeedKmh", label: "Max speed (0-255 km/h)", type: "number", min: 0, max: 255, required: true },
      { key: "durationSeconds", label: "Overspeed duration (0-255 sec)", type: "number", min: 0, max: 255, required: true },
    ],
    warning: "This sets the limit on the device itself. Platform alerts keep using the client-level overspeed limit configured in the admin console.",
  },
  TRACKER_CUT_FUEL: {
    label: "Cut Off Fuel",
    category: "TRACKER_CONTROL",
    safety: "DANGEROUS",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
    warning: "This immobilizes the vehicle by cutting the fuel/electric relay. Only use when the vehicle is safely stopped.",
    requireConfirmText: true,
  },
  TRACKER_RESTORE_FUEL: {
    label: "Restore Fuel Supply",
    category: "TRACKER_CONTROL",
    safety: "WARNING",
    delivery: "QUEUE_WHEN_OFFLINE",
    fields: [],
    warning: "Re-enables the fuel/electric relay so the vehicle can start.",
  },
  TRACKER_FACTORY_RESET: {
    label: "Factory Reset Tracker",
    category: "TRACKER_CONTROL",
    safety: "DANGEROUS",
    delivery: "IMMEDIATE_ONLY",
    fields: [],
    warning: "The tracker reverts to factory settings and restarts. Server, APN and reporting configuration may need to be re-provisioned.",
    requireConfirmText: true,
  },
});

// Alert switch options for P40 fields
export const ALERT_SWITCH_OPTIONS = [
  { value: 0, label: "Disabled" },
  { value: 1, label: "GPRS Only" },
  { value: 2, label: "SMS Only" },
  { value: 3, label: "GPRS + SMS" },
];
